/**
 * The animation-block contract.
 *
 * A ```anim fence is the escape hatch for things a plot spec structurally
 * cannot draw: a ball rebounding, a Fourier series assembling itself, charge
 * draining through a circuit. Anything that is a function, a field, or a
 * trajectory belongs in ```plot instead.
 *
 * A block arrives in one of two states. The tutor writes a **brief** — metadata
 * only, no sketch — because writing physics prose and debugging p5 in the same
 * pass produces bad prose and worse p5. A dedicated animator fills the sketch in
 * afterwards, and the block becomes **ready**.
 *
 * The sketch is model-authored JavaScript, so the security boundary is the
 * cross-origin sandbox it runs in (see `server/sandbox/`), *not* this file.
 * What happens here is shape and size.
 */

import { z } from "zod";

/** Guard rails. The sandbox enforces the real ones; these keep payloads sane. */
export const ANIM_LIMITS = {
  maxCodeLength: 16_000,
  maxTitleLength: 160,
  maxCaptionLength: 600,
  maxBriefLength: 800,
  /** Wider than 3:1 or taller than 5:2 stops being a figure and starts being a banner. */
  minAspect: 0.4,
  maxAspect: 3,
} as const;

export const DEFAULT_ASPECT = 16 / 9;

/**
 * Constructs with no legitimate use inside a sketch, every one of which is
 * already inert in the sandbox — an opaque origin has no cookies or storage to
 * read, and `connect-src 'none'` drops every outbound request.
 *
 * They are refused anyway because a blocked figure that says *why* is worth far
 * more than one that renders an empty canvas, and because a hit here is worth
 * seeing in the logs.
 */
const REFUSED_TOKENS: readonly string[] = [
  "document.cookie",
  "localStorage",
  "sessionStorage",
  "indexedDB",
  "XMLHttpRequest",
  "importScripts",
  "serviceWorker",
  "window.parent",
  "window.top",
  "postMessage",
  "eval(",
  "new Function",
  "document.write",
  "fetch(",
  "import(",
];

export interface AnimSpec {
  readonly title?: string;
  readonly caption?: string;
  /** What the tutor asked for. Also the key the finished sketch is spliced by. */
  readonly brief?: string;
  /** Width ÷ height. The frame is locked to this, so the sketch is responsive. */
  readonly aspect: number;
  /** Empty while the block is still a brief. */
  readonly code: string;
}

export type AnimParseResult =
  /** A sketch, validated and ready to run. */
  | { readonly kind: "ready"; readonly spec: AnimSpec }
  /** A brief with no sketch yet — the animator has not finished. */
  | { readonly kind: "pending"; readonly spec: AnimSpec }
  | { readonly kind: "error"; readonly error: string };

const aspectSchema = z
  .union([z.number(), z.string()])
  .transform((raw, ctx) => {
    const ratio = typeof raw === "number" ? raw : parseRatio(raw);
    if (ratio === null) {
      ctx.addIssue({ code: "custom", message: 'expected "16:9" or a number' });
      return z.NEVER;
    }
    return ratio;
  })
  .refine(
    (ratio) => ratio >= ANIM_LIMITS.minAspect && ratio <= ANIM_LIMITS.maxAspect,
    `must be between ${ANIM_LIMITS.minAspect} and ${ANIM_LIMITS.maxAspect}`,
  );

const metaSchema = z.object({
  title: z.string().max(ANIM_LIMITS.maxTitleLength).optional(),
  caption: z.string().max(ANIM_LIMITS.maxCaptionLength).optional(),
  brief: z.string().max(ANIM_LIMITS.maxBriefLength).optional(),
  aspect: aspectSchema.optional(),
});

/**
 * Parse the raw text inside an ```anim fence.
 *
 * `key: value` front matter, then either a `---` line and a sketch, or nothing
 * — the brief-only form the tutor writes. Front matter is only recognised when
 * *every* line above the separator is a key/value pair, so a sketch that opens
 * with a labelled statement never has its first lines eaten as metadata.
 */
export function parseAnimSpec(raw: string): AnimParseResult {
  const { meta, code } = splitFrontMatter(raw);

  const parsed = metaSchema.safeParse(meta);
  if (!parsed.success) {
    return { kind: "error", error: formatIssues(parsed.error) };
  }

  const base = {
    ...(parsed.data.title !== undefined ? { title: parsed.data.title } : {}),
    ...(parsed.data.caption !== undefined ? { caption: parsed.data.caption } : {}),
    ...(parsed.data.brief !== undefined ? { brief: parsed.data.brief } : {}),
    aspect: parsed.data.aspect ?? DEFAULT_ASPECT,
  };

  const trimmed = code.trim();

  if (trimmed.length === 0) {
    if (parsed.data.brief === undefined) {
      return { kind: "error", error: "empty animation block" };
    }
    return { kind: "pending", spec: { ...base, code: "" } };
  }

  const problem = validateSketch(trimmed);
  if (problem !== null) return { kind: "error", error: problem };

  return { kind: "ready", spec: { ...base, code: trimmed } };
}

/** Everything a sketch must clear before it is allowed near the sandbox. */
export function validateSketch(code: string): string | null {
  const trimmed = code.trim();

  if (trimmed.length === 0) return "sketch is empty";

  if (trimmed.length > ANIM_LIMITS.maxCodeLength) {
    return `sketch is ${trimmed.length} characters; the limit is ${ANIM_LIMITS.maxCodeLength}`;
  }

  const refused = REFUSED_TOKENS.find((token) => trimmed.includes(token));
  if (refused !== undefined) {
    return `sketch uses "${refused}", which is not available inside a figure`;
  }

  // A sketch with no setup() never starts, and the frame would sit blank with
  // no explanation. Catching it here turns that into a sentence.
  if (!/\bfunction\s+setup\s*\(/.test(trimmed)) {
    return "sketch has no setup() function";
  }

  if (!/\bfunction\s+draw\s*\(/.test(trimmed)) {
    return "sketch has no draw() function";
  }

  return null;
}

/** `"16:9"` → 1.777…, `"1.5"` → 1.5. Null when it is neither. */
export function parseRatio(raw: string): number | null {
  const colon = /^\s*(\d+(?:\.\d+)?)\s*:\s*(\d+(?:\.\d+)?)\s*$/.exec(raw);
  if (colon) {
    const width = Number(colon[1]);
    const height = Number(colon[2]);
    if (height === 0) return null;
    return width / height;
  }

  const plain = Number(raw.trim());
  return Number.isFinite(plain) && plain > 0 ? plain : null;
}

const KEY_VALUE = /^([a-zA-Z][a-zA-Z0-9_]*)\s*:\s*(.*)$/;

function splitFrontMatter(raw: string): {
  meta: Record<string, string>;
  code: string;
} {
  const lines = raw.split("\n");
  const separator = lines.findIndex((line) => line.trim() === "---");

  // No separator: either the whole block is a brief, or the whole block is a
  // sketch. Metadata lines are the tell, and they must be *all* of it.
  if (separator === -1) {
    const meta = readMeta(lines);
    return meta === null ? { meta: {}, code: raw } : { meta, code: "" };
  }

  const meta = readMeta(lines.slice(0, separator));
  // One non-metadata line above the `---` and that head is code which happened
  // to sit above a separator. Bail rather than silently truncating the sketch.
  if (meta === null) return { meta: {}, code: raw };

  return { meta, code: lines.slice(separator + 1).join("\n") };
}

/** Null when any non-empty line is not a `key: value` pair. */
function readMeta(lines: readonly string[]): Record<string, string> | null {
  const content = lines.filter((line) => line.trim().length > 0);
  if (content.length === 0) return null;

  const meta: Record<string, string> = {};
  for (const line of content) {
    const match = KEY_VALUE.exec(line.trim());
    if (!match) return null;
    meta[match[1]!] = match[2]!.trim();
  }
  return meta;
}

function formatIssues(error: z.ZodError): string {
  return error.issues
    .slice(0, 4)
    .map((issue) => {
      const path = issue.path.join(".");
      return path ? `${path}: ${issue.message}` : issue.message;
    })
    .join("; ");
}
